import { Flex, Button, Loader, Text, Switch, Box } from '@mantine/core';
import { FormTextField, FormDataRange, FormRichTextEditor } from '@shared/ui';
import { FormGuideScheduleWork } from '@entities/scheduleWork';
import { FormGuideEmploymentType } from '@entities/employmentType';
import { useCreateVacancy } from '@entities/vacancy';
import { useShowMe } from '@entities/auth';
import { useForm, zodResolver } from '@mantine/form';
import { ValidateCreateVacancySchema } from './types';
import { showNotification } from '@shared/utils';
import { QueryKey } from '@shared/config';
import { useQueryClient } from '@tanstack/react-query';
import { FormCompanySelect } from '@entities/company';
import { IconChevronDown } from '@tabler/icons-react';
import { VacancyResponsibleUser } from '../vacancyResponsibleUser';
import { useGetUsers } from '@entities/user';
import { INITIAL_VALUES } from '../constants';
import { VacancyCandidatesCount } from '../vacancyCandidatesCount/vacancyCandidatesCount';
import { FormSalarySelectField } from '@widgets/form';
import { FormTagsInputSkills } from '@entities/skill/components/FormTagsInputSkills';
import { useGetSkill } from '@entities/skill';
import { getTagsByGroupsForCreate } from '@shared/helpers';
import { FormCitiesMultiSelect } from '@entities/region';
import { FormActivitySphereMultiselect } from '@entities/activitySphere';
import { EditVacancyTariff } from '../editVacancyTariff';
import { useLocation } from 'react-router';

type VacancyCreateProps = {
  onBackClick: () => void;
};

export const VacancyCreate = ({ onBackClick }: VacancyCreateProps) => {
  const { data: user } = useShowMe();
  const queryClient = useQueryClient();
  const { mutate: createVacancy, isPending } = useCreateVacancy();
  const location = useLocation();

  const form = useForm({
    mode: 'controlled',
    initialValues: location.state ?? INITIAL_VALUES,
    validate: zodResolver(ValidateCreateVacancySchema),
  });

  const { data: skills } = useGetSkill();

  const handleSubmit = (values: typeof form.values) => {
    if (
      !user ||
      values.cityIds.length === 0 ||
      !values.currencyId ||
      !values.salaryTo ||
      !values.salaryFrom ||
      !values.responsibleUserId ||
      !values.paymentPeriodId ||
      !values.tariffId
    ) {
      return;
    }

    createVacancy(
      {
        ...values,
        skills: getTagsByGroupsForCreate({
          directory: skills || [],
          selectedDirectory: values.skills,
        }),
        activitySphereIds: values.activitySphereIds.map((id: string) =>
          Number(id)
        ),
        requiredCandidatesCount: Number(values.requiredCandidatesCount),
        responsibleUserId: Number(values.responsibleUserId),
        cityIds: values.cityIds.map(Number),
        currencyId: Number(values.currencyId),
        salaryFrom: values.salaryFrom,
        salaryTo: values.salaryTo,
        activeTo: values.activeTo?.toISOString() || '',
        activeFrom: values.activeFrom?.toISOString() || '',
        paymentPeriodId: Number(values.paymentPeriodId),
        companyId: Number(values.companyId),
        tariffId: Number(values.tariffId),
        name: values.name || '',
        mandatoryRequirements: values.mandatoryRequirements || '',
        additionalRequirements: values.mandatoryRequirements || '',
        responsibilities: values.responsibilities || '',
        ai_sphere_fill: values.ai_sphere_fill || false,
        employmentTypeIds: values.employmentTypeIds || [],
        scheduleWorkIds: values.scheduleWorkIds || [],
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [QueryKey.GET_VACANCIES],
          });

          queryClient.invalidateQueries({
            queryKey: [QueryKey.GET_SKILLS],
          });

          onBackClick();
        },
        onError: error => {
          let message =
            error.response?.data.message || 'Ошибка при создании вакансии';
          const errors = error.response?.data.data?.errors;

          if (errors?.['region.existing.id'] || errors?.['region.new.name']) {
            message = 'Нельзя выбрать несколько регионов';
          }

          showNotification({
            error,
            variant: 'error',
            message,
          });
        },
      }
    );
  };

  const { data: users, isLoading: isLoadingUsers } = useGetUsers(
    {
      filter: {
        'company.id': Number(form.values.companyId),
      },
    },
    Boolean(Number(form.values.companyId))
  );

  const responsibleUserOptions =
    users?.map(user => ({
      value: user.id.toString(),
      label: `${user?.profile?.firstName} ${user?.profile?.lastName}`,
    })) ?? [];

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Flex gap={14}>
        <Flex direction='column' gap={32}>
          <Flex gap={24} direction='column'>
            <Flex direction='column'>
              <FormCompanySelect
                placeholder='Выберите'
                key={form.key('companyId')}
                {...form.getInputProps('companyId')}
                error={form.errors.companyId}
              />

              <VacancyResponsibleUser
                key={form.key('responsibleUserId')}
                {...form.getInputProps('responsibleUserId')}
                role={user?.role?.name}
                data={responsibleUserOptions}
                error={form.errors.responsibleUserId}
                disabled={!form.values.companyId || isLoadingUsers}
                rightSection={
                  isLoadingUsers ? (
                    <Loader size='xs' />
                  ) : (
                    <IconChevronDown
                      size={20}
                      color='var(--mantine-color-primary-7)'
                    />
                  )
                }
              />

              <FormActivitySphereMultiselect
                title={
                  <Flex
                    justify='space-between'
                    align='center'
                    gap='md'
                    wrap='nowrap'
                    w='100%'
                  >
                    <Text
                      fw={600}
                      size='md'
                      c='var(--mantine-color-primary-7)'
                      style={{ flexShrink: 0 }}
                    >
                      Профессия
                    </Text>
                    <Switch
                      label='Автозаполнение'
                      {...form.getInputProps('ai_sphere_fill', {
                        type: 'checkbox',
                      })}
                      onChange={e => {
                        form
                          .getInputProps('ai_sphere_fill')
                          .onChange(e.target.checked);
                        if (e.target.checked) {
                          form.getInputProps('activitySphereIds').onChange([]);
                        }
                      }}
                    />
                  </Flex>
                }
                description='Выберите все подходящие профессии для вакансии'
                placeholder='Выберите'
                key={form.key('activitySphereIds')}
                {...form.getInputProps('activitySphereIds')}
                disabled={form.values.ai_sphere_fill}
                error={form.errors.activitySphereIds}
              />

              <FormTextField
                title='Название вакансии'
                description='Введите название должности, на которую Синтия будет искать лучшего кандидата'
                placeholder='Введите название'
                key={form.key('name')}
                {...form.getInputProps('name')}
                error={form.errors.name}
              />

              <FormRichTextEditor
                title='Трудовые обязанности'
                description='Опишите, какие задачи должен будет выполнять работник на работе, за что отвечать'
                placeholder='Введите описание'
                key={form.key('responsibilities')}
                {...form.getInputProps('responsibilities')}
                containerProps={{
                  mb: 0,
                }}
              />

              <VacancyCandidatesCount
                key={form.key('requiredCandidatesCount')}
                {...form.getInputProps('requiredCandidatesCount')}
                error={form.errors.requiredCandidatesCount}
              />

              <FormCitiesMultiSelect
                key={form.key('cityIds')}
                {...form.getInputProps('cityIds')}
                error={form.errors.cityIds}
              />

              <FormGuideEmploymentType
                key={form.key('employmentTypeIds')}
                {...form.getInputProps('employmentTypeIds')}
                error={form.errors.employmentTypeIds}
              />

              <FormGuideScheduleWork
                key={form.key('scheduleWorkIds')}
                {...form.getInputProps('scheduleWorkIds')}
                error={form.errors.scheduleWorkIds}
              />

              <FormSalarySelectField
                title='Уровень дохода'
                description='Важно указать реальный желаемый диапазон, чтобы ваш запрос получил больше откликов.'
                propsFromInput={{
                  ...form.getInputProps('salaryFrom'),
                  error: form.errors.salaryFrom,
                }}
                propsToInput={{
                  ...form.getInputProps('salaryTo'),
                  error: form.errors.salaryTo,
                }}
                propsCurrencyInput={{
                  ...form.getInputProps('currencyId'),
                  error: form.errors.currencyId,
                }}
                propsPaymentPeriodInput={{
                  ...form.getInputProps('paymentPeriodId'),
                  error: form.errors.paymentPeriodId,
                }}
              />

              <FormRichTextEditor
                title='Обязательные требования к кандидату'
                description='Укажите здесь только самые важные требования к кандидату, на которые Синтия должна ориентироваться в выборе. Кандидаты не соответствующие этим требованиям она не будет рассматривать. Например, для водителя обязательно нужны водительские права.'
                placeholder='Введите описание'
                key={form.key('mandatoryRequirements')}
                {...form.getInputProps('mandatoryRequirements')}
              />

              <FormRichTextEditor
                title='Дополнительные требования к кандидату'
                description='Добавьте специальные требования, важные для Вас, чтобы Синтия смогла уточнить их у кандидата и дать выбрать для Вас лучшего из лучших. Например, кандидат должен знать иностранный язык или иметь опыт работы в необходимой области более 5 лет, подтвержденный рекомендациями'
                placeholder='Введите описание'
                key={form.key('additionalRequirements')}
                {...form.getInputProps('additionalRequirements')}
              />

              <FormDataRange
                title='Начало работы (или период работы для временных вакансий)'
                description='Укажите дату, когда кандидат должен приступить к работе (или укажите период работы, если вакансия временная). Синтия будет подбирать только тех, кто готов выйти на работу в это время.'
                key={form.key('activeFrom')}
                propsFromInput={{
                  ...form.getInputProps('activeFrom'),
                  error: form.errors.activeFrom,
                }}
                propsToInput={{
                  ...form.getInputProps('activeTo'),
                  error: form.errors.activeTo,
                }}
              />

              <FormTagsInputSkills
                key={form.key('skills')}
                {...form.getInputProps('skills')}
                error={form.errors.skills}
              />

              {/* <FormTextarea
                title='Сообщение для заинтересованных кандидатов'
                description='Как только лучший кандидат будет найден Синтия свяжется с ним и предложит Вашу вакансию. Если кандидат подтвердит готовность поработать на Вашей вакансии, Синтия вышлет кандидату финальное сообщение из данного поля. Вы можете указать в этом поле адрес или прикрепите ссылку куда подойти кандидату для оформления, в какое время подойти и кого спросить.'
                placeholder='Введите место встречи'
                key={form.key('interviewFinalMessage')}
                {...form.getInputProps('interviewFinalMessage')}
              /> */}
            </Flex>
          </Flex>

          <Flex gap={8}>
            <Button variant='gray' onClick={onBackClick}>
              Назад
            </Button>
            <Button type='submit' loading={isPending} disabled={isPending}>
              Создать
            </Button>
          </Flex>
        </Flex>

        <Box pt={20} w='100%' maw={418}>
          <EditVacancyTariff {...form.getInputProps('tariffId')} />
        </Box>
      </Flex>
    </form>
  );
};
